"use client";

import useWindowSize from "@/utils/hooks/useWindowSize";
import EmblaSlider from "@/components/EmblaSlider";
import React from "react";
import "@/styles/emblaMainSlider.css";
import { Box, Button, InnerBannerFull, Text } from "@dxp-feed/ui";
import Link from "@/components/Link";
import { useLayoutEffect, useRef } from "@/utils/hooks/reactUseCustomHooks";
import { addDataLayer } from "@/helpers/dataLayer";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { useAppSelector } from "@/utils/hooks/reactUseCustomHooks";
import AdSlot from "@/helpers/gpt";
 
interface EmblaSliderProps {
  carouselData: any;
  carouselWrapperData: any;
}

export default function HeroSlider({
  carouselData,
  carouselWrapperData,
}: EmblaSliderProps) {
  const { width } = useWindowSize();
  const isClient = typeof window !== "undefined";
  const themeBgColor = carouselWrapperData?.themeBgColor;
  const textColor = carouselWrapperData?.textColor;
  const Sliderref = useRef<HTMLDivElement>(null);
  if (!isClient) return null;
  const { uid: Uid } = useAppSelector((state) => state.gigyaslice);

  const getImageUrl = (item: any) => {
    if (width <= 767) {
      return `${process.env.NEXT_PUBLIC_AEM_PATH_URL}${item?.mobileImage}`;
    } else if (width <= 1199) {
      return `${process.env.NEXT_PUBLIC_AEM_PATH_URL}${item?.tabletImage}`;
    } else {
      return `${process.env.NEXT_PUBLIC_AEM_PATH_URL}${item?.image}`;
    }
  };

  useLayoutEffect(() => {
    if (Sliderref.current) {
      const targetEle = Sliderref.current.querySelectorAll(".custom-container-class");
      targetEle.forEach((slide) => {
        const element = slide as HTMLElement;
        element.classList.remove("bg-brandSecondary");
        element.style.setProperty("background-color", themeBgColor, "important");
      });
    }
  }, [themeBgColor]);

  return (
    <Box as="div" role="banner">
      <EmblaSlider
        ref={Sliderref}
        align="center"
        options={{ loop: true, align: "start", dragFree: false }}
        slidesPerView={{ base: 1 }}
        slidesToScroll={{ base: 1 }}
        showControls={true}
        thumbnails={false}
        className="embla_mainSlider"
        carouselData={carouselData}
        bannerKey={true}
      >
        {carouselData?.map((item: any, index: number) => {
          const isImageLeft = item?.imagePosition?.toLowerCase() === "left";

          return (
            <AdSlot id={item?.gamId} key={`${item?.imagealt || "slide"}-${index}`} Fallback={carouselWrapperData.carouselType == "textType" ? (
                <InnerBannerFull
                  key={`${item?.imagealt || "slide"}-${index}`}
                  imageSrc={getImageUrl(item)}
                  imageAlt={item?.imagealt || "banner"}
                  className={`custom-container-class ${!isImageLeft ? "first-slide-style" : "second-slide-style"
                    }`}
                  contentClass={`gap-2 px-3 py-6 md:py-12 lg:py-4 lg:!max-w-[55%] !w-full ${!isImageLeft
                    ? "lg:pr-[50px] 6xl:pr-[108px] lg:pl-[40px] 6xl:pl-[0px] z-[999] contentData"
                    : "lg:pr-[40px] 6xl:pr-[0px] lg:pl-[50px] 6xl:pl-[108px] lg:ml-[45%] z-[999] contentData"
                    }`}
                  imageClass={`h-[180px] md:h-[270px] lg:h-full lg:!max-w-[45%] !w-full ${!isImageLeft ? "" : "left-0"
                    }`}
                  style={{ backgroundColor: themeBgColor }}
                >
                  <Text
                    as="p"
                    className={`text-sm w-full ${!isImageLeft ? "" : "lg:text-right"}`}
                    style={{ color: textColor }}
                  >
                    {item?.subheading}
                  </Text>

                  <Text
                    as="p"
                    className={`font-sans text-xl lg:text-2xl xl:text-3xl 5xl:text-4xl font-bold line-clamp-2 overflow-hidden lg:line-clamp-none lg:overflow-auto lg:mb-2 ${!isImageLeft ? "text-left" : "lg:text-right"
                      }`}
                    style={{ color: textColor }}
                  >
                    {item?.title}
                  </Text>

                  <Text
                    as="p"
                    className={`md:text-base w-full line-clamp-2 overflow-hidden lg:line-clamp-none lg:overflow-auto lg:mb-2 ${!isImageLeft ? "text-left" : "lg:text-right"
                      }`}
                    style={{ color: textColor }}
                    dangerouslySetInnerHTML={{
                      __html: item?.description || "",
                    }}
                  />

                  <Box
                    as="div"
                    className={`d-inline-block mt-2 flex gap-4 ${!isImageLeft ? "" : "lg:justify-end"
                      }`}
                  >
                    {item?.linktitle && (
                      <Button
                        asChild
                        variant="outline"
                        className="font-normal py-[9.5px] px-6 w-auto "
                        onClick={() => {
                          addDataLayer({
                            event: "banner_click",
                            click_text: item.imagealt || "default",
                            click_url: `${index + 1}`,
                            page_title: document.title,
                          });
                          sendSalesforceInteraction({
                            name: 'homepage_clicks',
                            banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                            clickUrl: `${item?.link}`,
                            clickText: item.imagealt || "default",
                            isAnonymous: Uid ? 0 : 1,
                            gigyaUid: Uid,
                            section: 'banner',
                            utmSource: getUTMParameter("utm_source"),
                            utmMedium: getUTMParameter("utm_medium"),
                            utmCampaign: getUTMParameter("utm_campaign"),
                          });
                        }}
                      >
                        <Link
                          href={item?.link || "#"}
                          target={item?.linktarget}
                          aria-label={item?.linkAriaLabel}
                          prefetch={false}
                          className="!py-[8.5px] 3xs:!px-5 sm:!px-6 border-white hover:border-white focus:border-white hover:!text-[--hover-color]"
                          style={
                            {
                              color: themeBgColor,
                              borderColor: textColor,
                              "--hover-color": textColor,
                            } as React.CSSProperties
                          }
                        >
                          {item?.linktitle}
                        </Link>
                      </Button>
                    )}

                    {item?.secondaryLinkTitle && (
                      <Button
                        asChild
                        variant="primary"
                        className="font-normal py-[9.5px] px-6 w-auto"
                        onClick={() => {
                          addDataLayer({
                            event: "banner_click",
                            click_text: item.title || "default",
                            click_url: `${index + 1}`,
                            page_title: document.title,
                          });
                          sendSalesforceInteraction({
                            name: 'homepage_clicks',
                            banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
                            clickUrl: `${item?.secondaryLink}`,
                            clickText: item.title || "default",
                            isAnonymous: Uid ? 0 : 1,
                            gigyaUid: Uid,
                            section: 'banner',
                            utmSource: getUTMParameter("utm_source"),
                            utmMedium: getUTMParameter("utm_medium"),
                            utmCampaign: getUTMParameter("utm_campaign"),
                          });
                        }}
                      >
                        <Link
                          href={item?.secondaryLink || "#"}
                          target={item?.secondaryLinkTarget}
                          aria-label={item?.secondaryAriaLabel}
                          prefetch={false}
                          className="!py-[8.5px] 3xs:!px-5 sm:!px-6 border-white hover:border-white focus:border-white hover:!text-[--hover-color]"
                          style={
                            {
                              color: textColor,
                              borderColor: textColor,
                              "--hover-color": themeBgColor,
                            } as React.CSSProperties
                          }
                        >
                          {item?.secondaryLinkTitle}
                        </Link>
                      </Button>
                    )}
                  </Box>

                  <Text
                    as="p"
                    className={`text-sm w-full pt-1 ${!isImageLeft ? "" : "lg:text-right"}`}
                    style={{ color: textColor }}
                    dangerouslySetInnerHTML={{
                      __html: item?.disclaimerText || "",
                    }}
                  />
                </InnerBannerFull>
              ) : (
                // type image prod
                <InnerBannerFull
                  key={`${item?.imagealt || "slide"}-${index}`}
                  imageSrc={getImageUrl(item)}
                  imageAlt={item?.imagealt || "banner"}
                  className={`custom-container-class first-slide-style `}
                  contentClass={`gap-2 px-3 py-0 md:py-0 lg:py-4 lg:!max-w-[55%] !w-full lg:pr-[50px] 6xl:pr-[108px] lg:pl-[40px] 6xl:pl-[0px] z-[999]`}
                  imageClass={`h-[180px] md:h-[270px] lg:h-full lg:!max-w-[100%] !w-full`}
                  style={{ backgroundColor: themeBgColor }}
                />
              )}/>
          );
        })}
      </EmblaSlider>
    </Box>
  );
}
